<script lang="ts">
export default {
  name: 'ValidationStep3',
  inheritAttrs: false,
  customOptions: {}
}
</script>

<script setup lang="ts">
import { ref, watch, nextTick } from 'vue';
import { useGlobalStore } from '../../stores/global';
import Address from '../../components/Address.vue';
import LoginTest from '../../components/LoginTest.vue'
import Checkbox from 'primevue/checkbox';
import { getReCaptchaToken } from '../../js/get_recaptcha_token';

const props = defineProps({
  url: {type: String, required: true},
  prev_url: {type: String, required: true},
  verify_email_url: {type: String, required: true},
  login_url: {type: String, required: true},
  forgot_password_url: { type: String, default: '' },
  result_url: {type: String, required: true},
  trans: {type: Object, required: true},
  select_options: {type: Object, required: true},
});

const formRef = ref<HTMLElement | null>(null);
const errorRef = ref<HTMLElement | null>(null);

const store = useGlobalStore();
let login_test_step = ref('verify_email');
let form_readonly = false;
if (store.user.id) {
  login_test_step.value = 'reaction';
  form_readonly = true;
}
let new_password = ref('');
let verify_password = ref('');

let saving = ref(false);
let error_msg = ref('');

watch(login_test_step, async (_new) => {
  switch (_new) {
    case 'success':
    case 'register':
      login_test_step.value = 'reaction';
      break;
    case 'reaction':
      await nextTick(() => {
        formRef.value?.querySelector('input')?.focus();
      });
      break;
  }
});

let validation_run = ref(false);
const validateForm = (): boolean => {
  validation_run.value = true;
  let u = store.user;
  if (!u.firstName || !u.lastName) {
    return false;
  }
  if (!u.company) {
    return false;
  }
  let a = u.address;
  if (!a.postcode || !a.number || !a.street || !a.town) {
    return false;
  }
  if (!u.email || !u.phone || !u.phoneMobile) {
    return false;
  }
  if (!store.reaction.agree_terms) {
    return false;
  }

  validation_run.value = false;

  return true;
}

const isReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const saveReaction = async () => {
  error_msg.value = '';

  if (!validateForm()) {
    await nextTick(() => {
      let el = formRef.value?.querySelector('.p-invalid') as HTMLElement;
      if (el) {
        el.scrollIntoView({ behavior: isReducedMotion ? 'auto' : 'smooth' });
      }
    })
    return;
  }
  saving.value = true;

  const token = await getReCaptchaToken('save_valuation');

  const res = await fetch(props.url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      'g-recaptcha-response': token,
      'user': store.user,
      'reaction': store.reaction,
      'new_password': new_password.value,
    }),
  });

  const data = await res.json();

  if (data.success) {
    // Just redirect to the thanks page. Don't show the success message.
    // success_msg.value = data.message;
    // saved.value = true;

    if (data.url !== '') {
      window.location.href = data.url;
    }
  } else {
    error_msg.value = data.message;
    errorRef.value?.scrollIntoView({behavior: isReducedMotion ? 'auto' : 'smooth'});
  }

  saving.value = false;
};
</script>

<template>
  <div class="flow">
    <p>{{ trans.your_info }}</p>
  </div>
  <form ref="formRef" class="form" id="valuation-form">
    <LoginTest v-if="login_test_step === 'verify_email' || login_test_step === 'login'"
               v-model:step="login_test_step" :verify_email_url="verify_email_url"
               :login_url="login_url" :forgot_password_url="forgot_password_url" :trans="trans"></LoginTest>

    <template v-else-if="login_test_step === 'reaction'">
      <div class="row">
        <div>
          <label for="reaction-form-email">{{ trans.email }}</label>
          <input v-model="store.user.email" id="reaction-form-email" type="email" placeholder="" required readonly disabled />
        </div>
      </div>

      <hr style="margin:0;">


      <div class="row">
        <div>
          <label for="reaction-form-company">{{ trans.company }}</label>
          <input v-model="store.user.company" id="reaction-form-company" :class="{'p-invalid': validation_run && !store.user.company}" type="text" required />
        </div>
      </div>

      <div class="row">
        <div>
          <label for="reaction-form-form-of-address">{{ trans.formOfAddress }}</label>
          <div class="inputs-group inline">
            <label v-for="fa in select_options.formOfAddress" class="ui-radio">
              <input v-model="store.user.formOfAddress" id="reaction-form-form-of-address" name="formOfAddress" type="radio" :value="fa.id">
              <span>{{ fa.label }}</span>
            </label>
          </div>
        </div>
      </div>

      <div class="row">
        <div class="size-md">
          <label for="reaction-form-first-name">{{ trans.first_name }}</label>
          <input v-model="store.user.firstName" :class="{'p-invalid': validation_run && !store.user.firstName} "
                 id="reaction-form-first-name" type="text" placeholder="" required :readonly="form_readonly" />
        </div>
        <div>
          <label for="reaction-form-last-name">{{ trans.last_name }}</label>
          <input v-model="store.user.lastName" :class="{'p-invalid': validation_run && !store.user.lastName}"
                 id="reaction-form-last-name" type="text" placeholder="" required :readonly="form_readonly" />
        </div>
      </div>

      <Address :trans="trans.address" :validation_run v-model:postcode="store.user.address.postcode"
               v-model:number="store.user.address.number"
               v-model:street="store.user.address.street"
               v-model:town="store.user.address.town" />

      <div class="row">
        <div>
          <label for="reaction-form-phone">{{ trans.phone }}</label>
          <input v-model="store.user.phone" :class="{'p-invalid': validation_run && !store.user.phone}" id="reaction-form-phone" type="tel" :readonly="form_readonly" />
        </div>

        <div>
          <label for="reaction-form-phone-mobile">{{ trans.mobile }}</label>
          <input v-model="store.user.phoneMobile" :class="{'p-invalid': validation_run && !store.user.phoneMobile}" id="reaction-form-phone-mobile" type="tel" :readonly="form_readonly" />
        </div>
      </div>

      <hr style="margin:0;">

      <template v-if="!store.user.id">
        <div class="flow">
          <h2>{{ trans.create_password }}</h2>

          <p>{{ trans.new_account }}</p>
        </div>
        <div class="row">
          <div>
            <label for="reaction-form-new-password">{{ trans.password }}</label>
            <input v-model="new_password" :class="{'p-invalid': validation_run && !new_password}"
                   id="reaction-form-new-password" type="password" minlength="6"
                   autocomplete="new-password" placeholder="" required>
          </div>

          <div>
            <label for="reaction-form-verify-password">{{ trans.password_repeat }}</label>
            <input v-model="verify_password" :class="{'p-invalid': validation_run && !(verify_password && (new_password === verify_password))}"
                   id="reaction-form-verify-password" type="password" minlength="6"
                   autocomplete="new-password" placeholder="" required>
          </div>
        </div>

        <hr style="margin:0;">
      </template>

      <div>
        <h2 class="h2" v-html="trans.form_title"></h2>
        <p v-html="trans.form_text"></p>
      </div>

      <label v-if="false" class="checkbox-wrap">
        <Checkbox v-model="store.reaction.register_newsletter" binary/>
        <span v-html="trans.register_newsletter"></span>
      </label>

      <label class="checkbox-wrap">
        <Checkbox v-model="store.reaction.agree_terms" :invalid="validation_run && !store.reaction.agree_terms" binary/>
        <span v-html="trans.agree_terms"></span>
      </label>

      <div ref="errorEl" v-if="error_msg" class="notice notice-error" v-html="error_msg"></div>

      <div class="form-controls">
        <a :href="prev_url" class="link link--arrow-l">
          <span>{{ trans.prev }}</span>
        </a>

        <button :disabled="saving" @click.prevent="saveReaction" class="btn-solid icon-rarr"
                :class="saving ? 'icon-spinner' : 'icon-mail'">
          {{ trans.confirm }}
        </button>
      </div>
    </template>
  </form>
</template>